import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Slider } from "@/components/ui/slider";
import { ArrowRight, Sparkles } from "lucide-react";

const fmtUSD = (n) =>
  n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000
    ? `$${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k`
    : `$${Math.round(n).toLocaleString()}`;

export default function RoiCalculator() {
  const [missedCalls, setMissedCalls] = useState(120);
  const [avgTicket, setAvgTicket] = useState(450);
  const [closeRate, setCloseRate] = useState(35);

  const recovered = useMemo(() => {
    const monthly = (missedCalls * avgTicket * (closeRate / 100));
    return {
      monthly,
      annual: monthly * 12,
      roiVsGrowth: (monthly - 997) / 997, // Growth tier $997
      paybackDays: monthly > 0 ? Math.max(1, Math.round((997 / monthly) * 30)) : 999,
    };
  }, [missedCalls, avgTicket, closeRate]);

  return (
    <div className="grain min-h-screen flex flex-col">
      <Nav />
      <main className="flex-1 py-16 lg:py-24">
        <div className="max-w-[1080px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14">
          <header className="lg:col-span-5">
            <span className="tag mb-4"><Sparkles size={12} className="text-rust" /> ROI calculator</span>
            <h1 className="display text-5xl lg:text-6xl mt-6 leading-[1]">
              Run the math on <span className="display-italic text-rust">your shop</span>.
            </h1>
            <p className="text-ink/70 mt-5 max-w-[40ch]">
              Three sliders. Your monthly recovered revenue, year-over-year, and break-even day on the Growth tier.
            </p>

            <div className="mt-10 card-ink rounded-2xl p-6">
              <div className="mono text-[10px] uppercase tracking-wider text-cream/60">Live result</div>
              <div className="mt-3 grid grid-cols-2 gap-4">
                <div>
                  <div className="display text-5xl text-cream" data-testid="roi-monthly">{fmtUSD(recovered.monthly)}</div>
                  <div className="text-xs text-cream/60 mt-1">recovered / month</div>
                </div>
                <div>
                  <div className="display text-5xl text-ember" data-testid="roi-annual">{fmtUSD(recovered.annual)}</div>
                  <div className="text-xs text-cream/60 mt-1">recovered / year</div>
                </div>
              </div>
              <div className="h-px bg-cream/10 my-5"></div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-cream/60 mono text-[10px] uppercase tracking-wider">Payback</div>
                  <div className="display text-2xl text-cream mt-1">{recovered.paybackDays === 999 ? "—" : `${recovered.paybackDays} days`}</div>
                </div>
                <div>
                  <div className="text-cream/60 mono text-[10px] uppercase tracking-wider">ROI vs Growth ($997/mo)</div>
                  <div className="display text-2xl text-ember mt-1">{recovered.roiVsGrowth > 0 ? `${Math.round(recovered.roiVsGrowth * 100)}%` : "—"}</div>
                </div>
              </div>
            </div>
          </header>

          <section className="lg:col-span-7 card-paper rounded-2xl p-8 lg:p-10">
            <SliderRow
              testId="roi-slider-calls"
              label="Missed touches per month"
              hint="Calls + DMs + form submissions you don't answer in 5 min"
              value={missedCalls}
              onChange={setMissedCalls}
              min={10}
              max={600}
              step={10}
              format={(v) => `${v}`}
            />
            <SliderRow
              testId="roi-slider-ticket"
              label="Average ticket value"
              hint="Lifetime customer value works even better"
              value={avgTicket}
              onChange={setAvgTicket}
              min={50}
              max={10000}
              step={50}
              format={(v) => `$${v.toLocaleString()}`}
            />
            <SliderRow
              testId="roi-slider-close"
              label="Recovery / close rate"
              hint="Conservative for service businesses is 25–40%"
              value={closeRate}
              onChange={setCloseRate}
              min={5}
              max={70}
              step={1}
              format={(v) => `${v}%`}
            />

            <div className="mt-10 pt-6 border-t border-ink/10 flex flex-wrap items-center gap-3">
              <Link
                to="/audit"
                className="btn-primary px-6 py-3.5 rounded-full text-sm font-medium inline-flex items-center gap-2"
                data-testid="roi-cta-audit"
              >
                Get the AI audit on top of this <ArrowRight size={14} />
              </Link>
              <Link to="/" className="btn-ghost px-6 py-3.5 rounded-full text-sm font-medium">
                Back to home
              </Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function SliderRow({ label, hint, value, onChange, min, max, step, format, testId }) {
  return (
    <div className="py-6 border-b border-ink/10 last:border-b-0">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="form-label">{label}</div>
          <p className="text-xs text-mute mt-1 max-w-[40ch]">{hint}</p>
        </div>
        <div className="display text-3xl text-rust" data-testid={`${testId}-value`}>{format(value)}</div>
      </div>
      <Slider
        value={[value]}
        onValueChange={(v) => onChange(v[0])}
        min={min}
        max={max}
        step={step}
        className="mt-5"
        data-testid={testId}
      />
    </div>
  );
}
